// @ts-strict

import { Component, OnDestroy, OnInit } from '@angular/core';
import { of, Subject, throwError } from 'rxjs';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ExportReportsService, SingleStatisticsReport } from '../export-reports.service';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { NotificationTypes } from '@app/core/services/global-notifications/global-notifications.model';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, OnDestroy {
  public reports: SingleStatisticsReport[] = [];

  public isLoading = false;
  public isError = false;

  public searchValue = '';

  private destroy$ = new Subject<void>();

  constructor(
    private readonly exportReportsService: ExportReportsService,
    private readonly globalNotificationsService: GlobalNotificationsService
  ) {}

  ngOnInit(): void {
    this.getReportsList().subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public handleSearch = (searchValue: string) => {
    this.searchValue = searchValue;
  };

  public handleDeleteReport = (fileName: string): void => {
    this.exportReportsService
      .deleteReport(fileName)
      .pipe(
        mergeMap(res => {
          if (res.status_code === 200) {
            this.showNotification('successfully_deleted', false);

            return this.getReportsList();
          }

          this.showNotification('error_occured', true);

          return of({});
        }),
        catchError(err => {
          this.showNotification('error_occured', true);
          return throwError(err);
        })
      )
      .subscribe();
  };

  public handleDownloadReport(fileName: string): void {
    this.exportReportsService
      .downloadReport(fileName)
      .pipe(
        tap(({ body }) => {
          if (body) {
            const downloadURL = window.URL.createObjectURL(body);
            const link = document.createElement('a');

            link.href = downloadURL;
            link.download = fileName;
            link.click();

            this.showNotification('successfully_downloaded', false);
          }
        }),
        catchError(err => {
          this.showNotification('error_occured', true);

          return throwError(err);
        })
      )
      .subscribe();
  }

  private getReportsList = () => {
    this.isLoading = true;

    return this.exportReportsService.currentStatisticsSection$.pipe(
      switchMap(type => this.exportReportsService.getReportsList(type)),
      map(({ status, data }) => {
        this.isLoading = false;

        if (status === 'success' && data?.reports) {
          this.isError = false;

          this.reports = data.reports;

          return;
        }

        this.isError = true;
      }),
      catchError(err => {
        this.isLoading = false;
        this.isError = true;

        return throwError(err);
      }),
      takeUntil(this.destroy$)
    );
  };

  private showNotification(message: string, isError: boolean): void {
    this.globalNotificationsService.show({
      message: `view_translations.${message}`,
      type: isError ? NotificationTypes[1] : NotificationTypes[0],
      translate: true,
      isPersistent: false,
      id: ''
    });
  }
}
